import { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Box,
  Typography,
  Chip,
  LinearProgress,
  useTheme,
} from '@mui/material';
import {
  HelpCircle,
  X,
  Play,
  CheckCircle,
  Circle,
  Trophy,
} from 'lucide-react';
import { TourContext } from '../../contexts/TourContext';
import { ColorModeContext } from '../../App';

interface HelpSectionProps {
  onClose: () => void;
}

export function HelpModal({ onClose }: HelpSectionProps) {
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);
  const tourContext = useContext(TourContext);
  const theme = useTheme();
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  if (!tourContext) return null;

  const { tourGuides, startTour, tourProgress, getCompletionPercentage } = tourContext;

  const completionPercentage = getCompletionPercentage();
  const completedCount = Object.values(tourProgress).filter(p => p.completed).length;
  const totalCount = tourGuides.size;

  // Define tour guides for help modal
  const helpGuides = [
    {
      id: 'dashboard-overview',
      title: 'Dashboard Overview',
      description: 'Learn about your clinic dashboard',
    },
    {
      id: 'appointments-management',
      title: 'Managing Appointments',
      description: 'Create and manage patient appointments',
    },
    {
      id: 'patients-management',
      title: 'Managing Patients',
      description: 'Add, update, and manage patient profiles',
    },
    {
      id: 'staff-management',
      title: 'Managing Staff',
      description: 'Invite and manage clinic staff members',
    },
    {
      id: 'billing-operations',
      title: 'Billing & Payments',
      description: 'Manage patient billing and payments',
    },
    {
      id: 'reports-analytics',
      title: 'Reports & Analytics',
      description: 'View clinic reports and analytics',
    },
    {
      id: 'clinic-setup',
      title: 'Clinic Setup',
      description: 'Initial clinic configuration',
    },
  ];

  const handleStartTour = (guideId: string) => {
    startTour(guideId);
    onClose();
  };

  const selectedGuideData = selectedGuide ? helpGuides.find((g) => g.id === selectedGuide) : null;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpCircle size={24} />
            <Typography variant="h6" fontWeight={700}>
              {selectedGuideData ? 'Tour Guide' : 'Help & Tutorials'}
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            size="small"
            sx={{ minWidth: 'auto', p: 0.5 }}
          >
            <X size={18} />
          </Button>
        </Box>

        {!selectedGuideData && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Tour Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {completionPercentage === 100 && <Trophy size={16} color={theme.palette.success.main} />}
                <Typography variant="body2" fontWeight={600} color="primary">
                  {completedCount} / {totalCount} Completed
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{ height: 6, borderRadius: 3 }}
            />
            {completionPercentage === 100 && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                🎉 All tours completed! You're a pro!
              </Typography>
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        {!selectedGuideData ? (
          // List of available guides
          <List sx={{ width: '100%' }}>
            {helpGuides.map((guide) => {
              const isCompleted = tourProgress[guide.id]?.completed;
              const completionCount = tourProgress[guide.id]?.completionCount || 0;

              return (
                <ListItemButton
                  key={guide.id}
                  onClick={() => setSelectedGuide(guide.id)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 1,
                    border: '1px solid',
                    borderColor: isDark ? '#334155' : '#e2e8f0',
                    '&:hover': {
                      background: isDark ? '#1e293b' : '#f1f5f9',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {isCompleted ? (
                      <CheckCircle size={20} color={theme.palette.success.main} />
                    ) : (
                      <Circle size={20} color={theme.palette.text.secondary} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body1" fontWeight={600}>
                          {guide.title}
                        </Typography>
                        {isCompleted && (
                          <Chip
                            label="Completed"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {completionCount > 1 && (
                          <Chip
                            label={`×${completionCount}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={guide.description}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ) : (
          // Guide details
          <Box>
            <Button
              onClick={() => setSelectedGuide(null)}
              variant="text"
              size="small"
              sx={{ mb: 2 }}
            >
              ← Back to Help Topics
            </Button>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                {selectedGuideData.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedGuideData.description}
              </Typography>
              <Chip
                label="Interactive Tour Available"
                color="primary"
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      {selectedGuideData && (
        <DialogActions sx={{ pt: 2 }}>
          <Button
            onClick={() => setSelectedGuide(null)}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            onClick={() => handleStartTour(selectedGuide!)}
            variant="contained"
            startIcon={<Play size={16} />}
          >
            Start Tour
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
